// src/BerryStringUtil.ts
var spaces = memoize((nesting) => {
  return " ".repeat(nesting);
});
var defaultCallerSize = 20;
var multiLinePad = "\n" + spaces(defaultCallerSize + 3);
function indentMultiLine(messages) {
  return messages.map(indentString);
}
function padTrimCaller(caller, callerSize = defaultCallerSize) {
  return (caller + spaces(callerSize)).slice(0, callerSize);
}
function indentString(m) {
  if (typeof m === "string") {
    return m.replace(/\n/g, multiLinePad);
  } else {
    return m;
  }
}
function memoize(fn) {
  const cache = /* @__PURE__ */ new Map();
  return function(...args) {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    } else {
      const value = fn(...args);
      cache.set(key, value);
      return value;
    }
  };
}

// src/ReplaceUndefined.ts
function replaceUndefined(obj, defaults) {
  const result = { ...defaults, ...removeUndefined(obj) };
  return result;
}
function removeUndefined(obj) {
  const result = { ...obj };
  for (const key in result) {
    if (result[key] === void 0) {
      delete result[key];
    }
  }
  return result;
}

// src/BerryPretty.ts
var defaultOptions = {
  compact: true,
  wrapLength: 40,
  maxDepth: 6,
  maxArray: 24,
  prototypes: false,
  precision: 4
};
var circularRef = {
  iAmCircular: true
};
if (typeof DOMRect === "undefined") {
  globalThis.DOMRect = function() {
  };
}
function pretty(value, options = {}) {
  const opts = replaceUndefined(options, defaultOptions);
  const { wrapLength, compact } = opts;
  const iterated = /* @__PURE__ */ new Map();
  let result = prettyToString(value, opts, iterated);
  if (compact && result.length > wrapLength) {
    result = prettyToString(value, { ...opts, compact: false }, iterated);
  }
  return result;
}
function prettyToString(value, opts, iterated = /* @__PURE__ */ new Map()) {
  const state = { nesting: 0, depth: 0, seen: /* @__PURE__ */ new Set(), iterated };
  if (isObject(value)) {
    state.seen.add(value);
  }
  return prettyRecursive(value, opts, state);
}
function prettyRecursive(value, options, state) {
  let result;
  if (state.depth++ >= options.maxDepth) {
    result = "...";
  } else if (isInteger(value)) {
    result = value.toFixed();
  } else if (typeof value === "number") {
    result = prettyFloat(value, options.precision);
  } else if (typeof value === "string") {
    result = `"${replaceSpecials(value)}"`;
  } else if (arrayIsh(value)) {
    result = arrayToString(value, options, state);
  } else if (value instanceof Date) {
    result = value.toLocaleString();
  } else if (value instanceof Function) {
    result = "function " + value.name + "()";
  } else if (isDomElement(value)) {
    result = value;
  } else if (value === circularRef) {
    result = "<circular reference>";
  } else if (value instanceof DOMRect) {
    const { top, left, height, width } = value;
    result = prettyRecursive({ top, left, height, width }, options, state);
  } else if (typeof value === "symbol") {
    result = value.toString();
  } else if (value instanceof Error) {
    result = errorToString(value, options, state);
  } else if (value instanceof Map || value && value.constructor === Map) {
    result = mapToString(value, options, state);
  } else if (value instanceof Set || value && value.constructor === Set) {
    result = setToString(value, options, state);
  } else if (value && value[Symbol.iterator]) {
    result = iterableToString(value, options, state);
  } else if (isInstance(value)) {
    result = instanceToString(value, options, state);
  } else if (isObject(value)) {
    result = objectToString(value, options, state);
  } else if (value === true) {
    result = "true";
  } else if (value === false) {
    result = "false";
  } else if (value === null) {
    result = "null";
  } else if (value === void 0) {
    result = "undefined";
  } else {
    result = value;
  }
  state.depth--;
  return result;
}
var specials = /* @__PURE__ */ new Map([
  ["	", "\\t"],
  ["\r", "\\r"],
  ["\n", "\\n"],
  ['"', '\\"']
]);
function replaceSpecials(s) {
  return [...s].map((c) => specials.get(c) || c).join("");
}
function replaceCircular(keyValues, seen) {
  return keyValues.map(([key, value]) => {
    if (testCircular(value, seen)) {
      return [key, circularRef];
    } else {
      return [key, value];
    }
  });
}
function testCircular(value, seen) {
  if (isObject(value)) {
    if (seen.has(value)) {
      return true;
    } else {
      seen.add(value);
    }
  }
  return false;
}
function isObject(value) {
  const valueType = typeof value;
  return value != null && (valueType === "object" || valueType === "function");
}
function arrayIsh(value) {
  return Array.isArray(value) || ArrayBuffer.isView(value);
}
function arrayToString(values, options, state) {
  if (options.compact || values.length <= 1) {
    return oneLineArray(values, options, state);
  } else {
    return multiLineArray(values, options, state);
  }
}
function oneLineArray(value, options, state) {
  const expanded = [...value];
  const values = expanded.slice(0, options.maxArray).map((v) => prettyRecursive(v, options, state));
  if (expanded.length > options.maxArray) {
    values.push("...");
  }
  return "[" + values.join(", ") + "]";
}
function multiLineArray(value, options, state) {
  const bracketIndent = spaces(state.nesting);
  state.nesting++;
  const values = [...value].slice(0, options.maxArray).map((v) => prettyRecursive(v, options, state));
  if (value.length > options.maxArray) {
    values.push("...");
  }
  const indent = spaces(state.nesting * 2);
  state.nesting--;
  const lastDex = values.length - 1;
  const indentedLines = values.map((v, i) => {
    const comma = i === lastDex ? "" : ",";
    return indent + v + comma + "\n";
  });
  const linesTogether = indentedLines.join("");
  const open = bracketIndent + "[\n";
  const close = bracketIndent + "]";
  return open + linesTogether + close;
}
function isClass(v) {
  return typeof v === "function" && /^class\s/.test(Function.prototype.toString.call(v));
}
function isInstance(value) {
  return value && isClass(value.constructor);
}
function instanceToString(value, options, state) {
  const record = objectToString(value, options, state);
  return `${value.constructor.name} ${record}`;
}
function objectToString(value, options, state) {
  const { compact, prototypes } = options;
  const rawKeyValues = prototypes ? allEntries(value) : Object.entries(value);
  const keyValues = replaceCircular(rawKeyValues, state.seen);
  if (compact) {
    return oneLineObj(keyValues, options, state);
  } else {
    return multiLineKV(keyValues, options, state);
  }
}
function oneLineObj(keyValues, options, state) {
  const kvStrings = keyValues.map(
    ([k, v]) => k + ": " + prettyRecursive(v, options, state)
  );
  return "{" + kvStrings.join(", ") + "}";
}
function multiLineKV(keyValues, opts, state, separator = ": ") {
  if (keyValues.length === 0) {
    return "{ }";
  }
  const braceNesting = state.nesting, braceIndent = spaces(braceNesting * 2), indent = braceIndent + "  ";
  state.nesting++;
  const kvStrings = keyValues.map(
    ([k, v]) => k + separator + prettyRecursive(v, opts, state)
  );
  state.nesting--;
  return formatKVs(kvStrings, braceIndent, indent);
}
function formatKVs(kvStrings, braceIndent, indent) {
  let indentedStrings;
  let firstLine;
  if (braceIndent === "") {
    firstLine = "{ ";
    const first = kvStrings[0];
    indentedStrings = kvStrings.slice(1).map((kv) => indent + kv) || [];
    indentedStrings.unshift(first);
  } else {
    firstLine = "{\n";
    indentedStrings = kvStrings.map((kv) => indent + kv);
  }
  return firstLine + indentedStrings.join(",\n") + "\n" + braceIndent + "}";
}
function allEntries(obj) {
  const keyValues = [];
  for (const key in obj) {
    const value = obj[key];
    keyValues.push([key, value]);
  }
  return keyValues;
}
function prettyFloat(value, precision = 4) {
  const str = value.toPrecision(precision);
  if (str.indexOf(".") === -1) {
    return str;
  }
  let last = str.length - 1;
  while (str[last] === "0") {
    last -= 1;
  }
  if (str[last] === ".") {
    last -= 1;
  }
  return str.slice(0, last + 1);
}
function isInteger(value) {
  if (typeof value !== "number") {
    return false;
  }
  if (Number.isInteger(value)) {
    return true;
  }
  return isFinite(value) && Math.floor(value) === value;
}
function errorToString(err, options, state) {
  const stack = err.stack?.split("\n").slice(1, 2)[0];
  return prettyRecursive(
    { err: err.name, message: err.message, stack },
    options,
    state
  );
}
function mapToString(map, options, state) {
  const rawKeyValues = [...map.entries()].map(([key, value]) => {
    const keyString = prettyRecursive(key, options, state);
    return [keyString, value];
  });
  const keyValues = replaceCircular(rawKeyValues, state.seen);
  return multiLineKV(keyValues, options, state, " -> ");
}
function setToString(set, options, state) {
  const values = [...set.values()].map((value) => {
    return prettyRecursive(value, options, state);
  });
  return "{" + values.join(", ") + "}";
}
function iterableToString(iterable, options, state) {
  let expanded = state.iterated.get(iterable);
  if (!expanded) {
    expanded = [...iterable];
    state.iterated.set(iterable, expanded);
  }
  return arrayToString(expanded, options, state);
}
function isDomElement(value) {
  if (typeof Element === "undefined") {
    return false;
  } else {
    return value instanceof Element || value instanceof Text;
  }
}

// src/BerryStack.ts
function callerName(stack, level = 1, showFile = false) {
  const lines = stack.split("\n");
  if (lines.length && lines[0].startsWith("Error")) {
    return callerFromChromeStack(lines, level, showFile);
  } else {
    return callerFromFirefoxStack(lines, level, showFile);
  }
}
function callerFromFirefoxStack(stackLines, level = 1, showFile = false) {
  showFile;
  if (stackLines.length < level) {
    return "";
  }
  const line = stackLines[level];
  const atDex = line.indexOf("@");
  if (atDex === -1) {
    return "";
  }
  return line.slice(0, atDex);
}
function callerFromChromeStack(stackLines, level = 1, showFile = false) {
  if (stackLines.length < level + 1) {
    return "";
  }
  const line = stackLines[level + 1];
  const afterAt = startAfterAt(line);
  if (!afterAt) {
    return "";
  }
  const paren = afterAt.indexOf(" (");
  if (paren > 0) {
    const fullFunctionName = afterAt.slice(0, paren);
    const functionName = afterDot(fullFunctionName);
    if (showFile) {
      const fileName = fileFromUrlLine(afterAt);
      return `${fileName}.${functionName}`;
    } else {
      return functionName;
    }
  } else {
    return fileFromUrlLine(afterAt);
  }
}
function fileFromUrlLine(text) {
  let start = 0;
  let end = text.length;
  const slash = text.lastIndexOf("/");
  if (slash > 0) {
    start = slash + 1;
    const lastColon = text.lastIndexOf(":");
    if (lastColon > 0) {
      const secondColon = text.lastIndexOf(":", lastColon - 1);
      if (secondColon > 0) {
        end = secondColon;
      } else {
        end = lastColon;
      }
    }
  }
  const fileWithSuffix = text.slice(start, end);
  return beforeDot(fileWithSuffix);
}
var at = "at ";
function startAfterAt(line) {
  const atDex = line.indexOf(at);
  if (atDex === -1) {
    return void 0;
  }
  const start = atDex + at.length;
  return line.slice(start);
}
function beforeDot(text) {
  const dot = text.lastIndexOf(".");
  if (dot > 0) {
    return text.slice(0, dot);
  } else {
    return text;
  }
}
function afterDot(text) {
  const dot = text.indexOf(".");
  if (dot > 0) {
    return text.slice(dot + 1);
  } else {
    return text;
  }
}

// src/DebugLog.ts
var dLog = dlog;
var dErr = derr;
var dWarn = dwarn;
var defaultOptions2 = {
  stackLevel: 0,
  showFile: false,
  nameSize: 20,
  compact: true,
  wrapLength: 40,
  maxDepth: 6,
  maxArray: 24,
  prototypes: false,
  precision: 4
};
function dlog(...items) {
  console.log(...dLogMessages({}, ...items));
}
function dlogOpt(options, ...items) {
  console.log(...dLogMessages(options, ...items));
}
function dsert(test, ...items) {
  if (!test) {
    const messages = dLogMessages({}, ...items);
    console.assert(test, messages);
  }
}
function derr(...items) {
  console.error(...dLogMessages({}, ...items));
}
function dwarn(...items) {
  console.warn(...dLogMessages({}, ...items));
}
function dLogDefaultOptions(options) {
  Object.assign(defaultOptions2, options);
}
function dlogMessagesTest(options, ...items) {
  return dLogMessages(options, ...items);
}
function dLogMessages(options, ...items) {
  const opts = replaceUndefined(options, defaultOptions2);
  const level = opts.stackLevel + 2;
  const messages = items.map((item) => {
    if (item != null && typeof item == "object") {
      return debugVars(item, opts);
    } else {
      return item;
    }
  });
  const caller = callerName(new Error().stack || "", level, opts.showFile);
  const fixedWidthCaller = padTrimCaller(caller, opts.nameSize);
  messages.unshift(`${fixedWidthCaller} |`);
  return indentMultiLine(messages);
}
function debugVars(vars, options) {
  const strings = Object.entries(vars).map(
    ([key, value]) => `${key}: ${pretty(value, options)}`
  );
  const multiline = strings.find((s) => s.includes("\n"));
  if (multiline) {
    return strings.join("\n");
  } else {
    return strings.join("  ");
  }
}
export {
  callerName,
  dErr,
  dLog,
  dLogDefaultOptions,
  dWarn,
  derr,
  dlog,
  dlogMessagesTest,
  dlogOpt,
  dsert,
  dwarn,
  pretty,
  prettyFloat
};
//# sourceMappingURL=index.js.map
